import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [initializing, setInitializing] = useState(true);

  const bootstrap = useCallback(async () => {
    const token = localStorage.getItem("ccams_token");
    if (!token) {
      setInitializing(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      localStorage.setItem("ccams_user", JSON.stringify(data));
    } catch (e) {
      localStorage.removeItem("ccams_token");
      localStorage.removeItem("ccams_user");
    } finally {
      setInitializing(false);
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("ccams_token", data.access_token);
      localStorage.setItem("ccams_user", JSON.stringify(data.user));
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = () => {
    localStorage.removeItem("ccams_token");
    localStorage.removeItem("ccams_user");
    setUser(null);
  };

  const refreshMe = async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    localStorage.setItem("ccams_user", JSON.stringify(data));
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, setUser, initializing, login, logout, refreshMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export const ROLE_LABELS = {
  admin: "Administrator",
  compliance_officer: "Compliance Officer",
  auditor: "Auditor",
  employee: "Employee",
};
